// Box Bot, the face of Chat with Settings.
//
// The drawing and animation live in @wavebox/assistant_character, shared with
// the Wavebox browser so both draw the same character. This hook only binds
// one to an element and keeps it in step with the page. Any number can be on
// screen at once; each element gets its own instance.
//
// The server owns what the assistant is doing: `data-state` carries it and
// `updated()` applies it (the element is `phx-update="ignore"`, which still
// merges data attributes and still calls `updated()`). Two states have no
// server signal because they are about the person, not the model: typing in
// the composer and dictating. Those are picked up here, only by an instance
// marked `data-reactive`, and only while the server says idle, so a reply in
// flight is never overridden by a keystroke.

import { createBoxbot } from '@wavebox/assistant_character'
import { LISTENING_EVENT } from '../lib/assistant_signals'
import { createBoxbotActivity } from '../lib/boxbot_activity.mjs'

// How long after the last keystroke he keeps leaning in.
const TYPING_LINGER_MS = 1400

// Long enough that the wave reads as a greeting, not part of the page arriving.
const GREET_DELAY_MS = 500

const COMPOSER_ID = 'message-input'
const WORKING = new Set(['thinking', 'tool', 'agentTyping'])

export default {
  mounted () {
    const { size, greet, reactive, interactive, trackPointer } = this.el.dataset

    this.serverState = this.el.dataset.state || 'idle'
    this.typing = false
    this.listening = false

    this.bot = createBoxbot(this.el, {
      size: Number(size) || undefined,
      state: this.serverState,
      theme: this.theme(),
      interactive: interactive === 'true',
      trackPointer: trackPointer !== 'false',
      // Every placement sits beside text that already names the assistant
      label: null
    })

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.activity = createBoxbotActivity(this.bot, {
      motion: this.motionAllowed(),
      calmIdle: this.el.dataset.calmIdle === 'true'
    })
    this.onMotionChange = () => this.activity.setMotion(this.motionAllowed())
    this.motionQuery.addEventListener('change', this.onMotionChange)
    document.addEventListener('visibilitychange', this.onMotionChange)
    this.apply()

    // "auto" follows Brainbox's own light/dark switch (ThemeManager keeps
    // data-theme on <html>), not the OS, which the two can disagree about.
    // The attribute is `data-appearance`, never `data-theme`: daisyUI styles
    // every [data-theme] element as a themed surface, background included.
    if (this.el.dataset.appearance === 'auto') {
      this.themeObserver = new MutationObserver(() => this.bot.setTheme(this.theme()))
      this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
    }

    if (reactive === 'true') {
      this.onInput = (event) => {
        if (event.target?.id !== COMPOSER_ID) { return }
        this.typing = true
        clearTimeout(this.typingTimer)
        this.typingTimer = setTimeout(() => { this.typing = false; this.apply() }, TYPING_LINGER_MS)
        this.apply()
      }
      this.onListening = (event) => {
        this.listening = Boolean(event.detail?.listening)
        this.apply()
      }
      this.onBlur = (event) => {
        if (event.target?.id !== COMPOSER_ID) { return }
        clearTimeout(this.typingTimer)
        this.typing = false
        this.apply()
      }
      document.addEventListener('input', this.onInput, true)
      document.addEventListener('focusout', this.onBlur, true)
      window.addEventListener(LISTENING_EVENT, this.onListening)
    }

    if (greet === 'true') {
      this.greetTimer = setTimeout(() => {
        if (this.motionAllowed()) { this.bot.trigger('wave') }
      }, GREET_DELAY_MS)
    }
  },

  updated () {
    const next = this.el.dataset.state || 'idle'
    const finished = WORKING.has(this.serverState) && next === 'idle'
    this.serverState = next
    this.apply()
    // A small acknowledgement that the turn is done
    if (finished && this.motionAllowed()) { this.bot.trigger('nod') }
  },

  destroyed () {
    clearTimeout(this.typingTimer)
    clearTimeout(this.greetTimer)
    this.themeObserver?.disconnect()
    this.motionQuery?.removeEventListener('change', this.onMotionChange)
    document.removeEventListener('visibilitychange', this.onMotionChange)
    if (this.onInput) { document.removeEventListener('input', this.onInput, true) }
    if (this.onBlur) { document.removeEventListener('focusout', this.onBlur, true) }
    if (this.onListening) { window.removeEventListener(LISTENING_EVENT, this.onListening) }
    this.activity?.destroy()
    this.bot?.destroy()
  },

  theme () {
    const pinned = this.el.dataset.appearance
    if (pinned === 'light' || pinned === 'dark') { return pinned }
    return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light'
  },

  motionAllowed () {
    return !this.motionQuery.matches && !document.hidden
  },

  apply () {
    if (this.serverState !== 'idle') {
      this.activity.update(this.serverState)
    } else if (this.listening) {
      this.activity.update('listening')
    } else {
      this.activity.update(this.typing ? 'userTyping' : 'idle')
    }
  }
}
